import { isRussia } from 'env'
import { FormikProps } from 'formik'
import { endpoints } from 'modules/endpoints'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import FormSelectShared from 'views/components/FormSelectShared'
import { SharedModalInputs } from 'views/components/SharedModal'
import { SharedModalContent, SharedModalSecondContent, SharedModalSubTitle } from 'views/components/SharedModal/styled'
import { ShortFormControl } from 'views/styled/common'
import { countryFlags } from '../countryFlags'
import { FormGroup, Input, Text } from '@agro-club/agroclub-shared'

interface Props {
  formik: FormikProps<any>
  isAverageRates?: boolean
  isEditModal?: boolean
}

const ControlLabelWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 2px;
  svg {
    height: 20px;
    width: 20px;
  }
`

const RangesWrapper = styled.div<{ itemsPerColumn: number }>`
  display: grid;
  row-gap: 16px;
  column-gap: 60px;
  grid-template: ${({ itemsPerColumn }) => `repeat(${itemsPerColumn}, 1fr) / repeat(2, 1fr)`};
  grid-auto-flow: column;
`
const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ColumnHeader = () => {
  const { t } = useTranslation('rates')
  return (
    <FormGroup label=" " labelWidth={80}>
      <InputWrapper>
        <Text style={{ flexGrow: 1, flexBasis: '100px' }}>{t('loadNorm')}</Text>
        <div>/</div>
        <Text style={{ flexGrow: 1, flexBasis: '100px' }}>{t('loadOverload')}</Text>
      </InputWrapper>
    </FormGroup>
  )
}

export const ModalContent: FC<Props> = ({ formik, isEditModal, isAverageRates }) => {
  const { t } = useTranslation('rates')
  const itemsPerColumn = Math.ceil(formik.values.rates.length / 2)

  const regionsFilter = useMemo(
    () => ({
      parent_id: formik.values.countryId,
    }),
    [formik.values.countryId],
  )

  const getCountryLabel = (dto) => {
    const Icon = countryFlags[dto.country_code]
    return (
      <ControlLabelWrapper>
        <Icon />
        &nbsp;
        {dto.name}
      </ControlLabelWrapper>
    )
  }

  return (
    <SharedModalContent>
      <SharedModalInputs>
        {isRussia && !isEditModal && (
          <ShortFormControl>
            <FormSelectShared
              label={t('modal.countries')}
              placeholder=""
              formik={formik}
              onChange={() => formik.setFieldValue('sale_regions', [])}
              fieldName="countryId"
              showSearch={false}
              getLabel={getCountryLabel}
              endpoint={endpoints.countries()}
              simpleApiFormat
            />
          </ShortFormControl>
        )}
        <FormSelectShared
          label={t('modal.regions')}
          formik={formik}
          fieldName="sale_regions"
          getLabel={(dto) => dto.name}
          endpoint={endpoints.provinces()}
          filter={regionsFilter}
          simpleApiFormat
          disabled={isAverageRates}
          multiple
        />

        <FormSelectShared
          label={t('modal.logistician')}
          formik={formik}
          fieldName="responsible_logisticians"
          getLabel={(dto) => dto.full_name}
          endpoint={endpoints.dispatchingLogisticians()}
          multiple
          disabled={isAverageRates}
        />
      </SharedModalInputs>

      <SharedModalSecondContent>
        <SharedModalSubTitle>{t('modal.subTitle')}</SharedModalSubTitle>
        <RangesWrapper itemsPerColumn={1}>
          <ColumnHeader />
          <ColumnHeader />
        </RangesWrapper>
        <RangesWrapper itemsPerColumn={itemsPerColumn}>
          {formik.values.rates.map((rate, index) => (
            <FormGroup
              label={rate.distance_range.upper}
              error={formik?.errors?.rates?.[index]?.rate || formik?.errors?.rates?.[index]?.rate_with_overload}
              labelWidth={80}
              key={rate.distance_range.upper}
            >
              <InputWrapper>
                <Input
                  invalid={!!formik?.errors?.rates?.[index]?.rate}
                  {...formik.getFieldProps(`rates[${index}].rate`)}
                  placeholder=""
                  type="number"
                />
                <div>/</div>
                <Input
                  invalid={!!formik?.errors?.rates?.[index]?.rate_with_overload}
                  {...formik.getFieldProps(`rates[${index}].rate_with_overload`)}
                  placeholder=""
                  type="number"
                />
              </InputWrapper>
            </FormGroup>
          ))}
        </RangesWrapper>
      </SharedModalSecondContent>
    </SharedModalContent>
  )
}
